import { supabase } from "@/integrations/supabase/client";

type TriggerType = 
  | 'new_lead' 
  | 'booking_confirmation' 
  | 'booking_reminder' 
  | 'follow_up' 
  | 'welcome' 
  | 'hair_analysis' 
  | 'callback_request';

interface AutomationPayload {
  trigger_type: TriggerType;
  phone: string;
  name?: string;
  treatment?: string;
  date?: string;
  email?: string;
  lead_id?: string;
  score?: number;
}

export const triggerWhatsAppAutomation = async (payload: AutomationPayload) => {
  try {
    // Validate phone number
    if (!payload.phone || payload.phone.length < 8) {
      console.log('Invalid phone number, skipping WhatsApp automation');
      return null;
    }

    // Clean phone number
    const cleanPhone = payload.phone.replace(/[\s\-()]/g, '');
    
    // Ensure phone starts with country code
    const formattedPhone = cleanPhone.startsWith('+') 
      ? cleanPhone.substring(1) 
      : cleanPhone.startsWith('0') 
        ? '32' + cleanPhone.substring(1) // Belgian default
        : cleanPhone;

    const { data, error } = await supabase.functions.invoke('whatsapp-automation', {
      body: {
        ...payload,
        phone: formattedPhone,
      },
    });

    if (error) {
      console.error('WhatsApp automation error:', error);
      return null;
    }

    console.log('WhatsApp automation triggered:', data);
    return data;
  } catch (err) {
    console.error('Error triggering WhatsApp automation:', err);
    return null;
  }
};

// Hook for React components
export const useWhatsAppAutomation = () => {
  const sendAutomation = async (payload: AutomationPayload) => {
    return triggerWhatsAppAutomation(payload);
  };

  const sendNewLeadMessage = async (params: {
    phone: string;
    name?: string;
    treatment?: string;
    email?: string;
    lead_id?: string;
  }) => {
    return sendAutomation({
      trigger_type: 'new_lead',
      ...params,
    });
  };

  const sendBookingConfirmation = async (params: {
    phone: string;
    name?: string;
    treatment?: string;
    date?: string;
  }) => {
    return sendAutomation({
      trigger_type: 'booking_confirmation',
      ...params,
    });
  };

  const sendCallbackRequest = async (params: {
    phone: string;
    name?: string;
  }) => {
    return sendAutomation({
      trigger_type: 'callback_request',
      ...params,
    });
  };

  const sendHairAnalysisFollowUp = async (params: {
    phone: string;
    name?: string;
    email?: string;
    lead_id?: string;
  }) => {
    return sendAutomation({
      trigger_type: 'hair_analysis',
      ...params,
    });
  };

  return {
    sendAutomation,
    sendNewLeadMessage,
    sendBookingConfirmation,
    sendCallbackRequest,
    sendHairAnalysisFollowUp,
  };
};
